import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Rectangle, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  RefreshCw, Trash2, Download, HardDrive, BarChart3, Map as MapIcon,
  Database, AlertCircle, Square,
} from 'lucide-react';
import { Button } from './button';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Format bytes as a human-readable size string.
const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

const cellKey = (c) => `${c.x}_${c.y}`;

// Cache-percentage gradient (red 0% → green 100%), matching
// rastercachevisualize.py so the admin UI tells the same story as the
// folium-based debug map.
const colorForPercentage = (pct) => {
  const r = Math.round(255 * (1 - pct));
  const g = Math.round(255 * pct);
  return `rgb(${r}, ${g}, 0)`;
};

// Usage-count colouring. Linear scale to the max in the dataset; cells
// with zero hits stay grey so we don't pretend "cold + cached" === "hot".
const colorForUsage = (count, max) => {
  if (!count || max <= 0) return '#9ca3af';
  const ratio = Math.min(1, count / max);
  const r = Math.round(255 * (1 - ratio));
  const g = Math.round(255 * ratio);
  return `rgb(${r}, ${g}, 0)`;
};

// Build a placeholder cell record for an (x, y) that isn't on disk yet.
// Lets the user select uncached cells from a drawn rectangle and feed
// them straight into the fetch endpoint.
const virtualCell = (x, y, rasterSize, allDataTypes) => ({
  x,
  y,
  bounds: [x * rasterSize, y * rasterSize, (x + 1) * rasterSize, (y + 1) * rasterSize],
  data_types: [],
  missing_data_types: [...allDataTypes],
  cache_percentage: 0,
  size_bytes: 0,
  usage_count: 0,
  last_used: null,
  virtual: true,
});

// Click-drag rectangle selector, matched to the cache grid. While active,
// disables map dragging so the gesture isn't ambiguous; on mouseup, calls
// onSelect with the leaflet bounds and exits via onDone (parent toggles
// the mode off so the map reverts to normal pan/click behaviour).
const RectangleSelector = ({ enabled, onSelect, onDone }) => {
  const map = useMap();
  const dragRef = useRef(null);  // {start: LatLng, current: LatLng}
  const rectRef = useRef(null);  // transient L.Rectangle while dragging

  useEffect(() => {
    if (!enabled) return undefined;

    // Disable normal drag + box-zoom so our handler owns mousedown.
    map.dragging.disable();
    map.boxZoom.disable();
    const container = map.getContainer();
    const prevCursor = container.style.cursor;
    container.style.cursor = 'crosshair';

    const clearRect = () => {
      if (rectRef.current) {
        map.removeLayer(rectRef.current);
        rectRef.current = null;
      }
    };

    const onMouseDown = (e) => {
      clearRect();
      dragRef.current = { start: e.latlng, current: e.latlng };
    };

    const onMouseMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.current = e.latlng;
      const bounds = L.latLngBounds([drag.start, drag.current]);
      if (rectRef.current) {
        rectRef.current.setBounds(bounds);
      } else {
        rectRef.current = L.rectangle(bounds, {
          color: '#1d4ed8',
          weight: 2,
          dashArray: '4 4',
          fillColor: '#3b82f6',
          fillOpacity: 0.12,
          interactive: false,
        }).addTo(map);
      }
    };

    const onMouseUp = (e) => {
      const drag = dragRef.current;
      dragRef.current = null;
      clearRect();
      if (!drag) return;
      const a = drag.start;
      const b = e.latlng;
      const minLat = Math.min(a.lat, b.lat);
      const maxLat = Math.max(a.lat, b.lat);
      const minLon = Math.min(a.lng, b.lng);
      const maxLon = Math.max(a.lng, b.lng);
      // Ignore tiny accidental drags — anything smaller than ~0.05° is
      // probably a misclick rather than an intentional region.
      if (Math.abs(maxLat - minLat) < 0.05 && Math.abs(maxLon - minLon) < 0.05) {
        onDone();
        return;
      }
      onSelect({ minLat, maxLat, minLon, maxLon });
      onDone();
    };

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      clearRect();
      dragRef.current = null;
      map.dragging.enable();
      map.boxZoom.enable();
      container.style.cursor = prevCursor;
    };
  }, [enabled, map, onSelect, onDone]);

  return null;
};

// Imperatively fits the map to the bounding box of all cached cells on
// initial load. Without this the world view masks Europe-scale data.
const FitToCells = ({ cells, nonce }) => {
  const map = useMap();
  useEffect(() => {
    if (!cells.length) return;
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    for (const c of cells) {
      const [a, b, cMax, dMax] = c.bounds;
      if (a < minLon) minLon = a;
      if (b < minLat) minLat = b;
      if (cMax > maxLon) maxLon = cMax;
      if (dMax > maxLat) maxLat = dMax;
    }
    if (!isFinite(minLon)) return;
    map.fitBounds([[minLat, minLon], [maxLat, maxLon]], { padding: [20, 20] });
  }, [nonce, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const SelectionPanel = ({
  selectedCells, allCells, dataTypes, busy, onFetch, onCleanup, onDownload, onClear,
}) => {
  const totalSize = selectedCells.reduce((acc, c) => acc + (c.size_bytes || 0), 0);
  const missingTypes = useMemo(() => {
    const set = new Set();
    for (const c of selectedCells) {
      for (const dt of c.missing_data_types || []) set.add(dt);
    }
    return Array.from(set).sort();
  }, [selectedCells]);

  if (selectedCells.length === 0) return null;
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm">
          <span className="font-semibold">{selectedCells.length}</span> cell
          {selectedCells.length === 1 ? '' : 's'} selected · {fmtBytes(totalSize)}
        </div>
        <Button size="sm" variant="ghost" onClick={onClear} className="rounded-xl">
          Clear selection
        </Button>
      </div>
      {missingTypes.length > 0 && (
        <div className="text-xs text-gray-700">
          <span className="font-medium">Missing across selection:</span>{' '}
          {missingTypes.join(', ')}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          onClick={() => onFetch(null)}
          disabled={busy}
          className="bg-gray-900 text-white rounded-xl"
        >
          <Database className="h-4 w-4 mr-1" />
          Fetch all missing
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onDownload}
          disabled={busy}
          className="rounded-xl"
        >
          <Download className="h-4 w-4 mr-1" />
          Download
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onCleanup}
          disabled={busy}
          className="rounded-xl text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Cleanup
        </Button>
      </div>
      {dataTypes.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
            Fetch a specific data type…
          </summary>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {dataTypes.map((dt) => (
              <button
                key={dt}
                onClick={() => onFetch([dt])}
                disabled={busy}
                className="px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                {dt}
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const CellRectangle = ({ cell, isSelected, color, onToggle, viewMode }) => {
  const [minLon, minLat, maxLon, maxLat] = cell.bounds;
  const bounds = [[minLat, minLon], [maxLat, maxLon]];
  // Highlighted (selected) cells get a thick blue stroke; otherwise colour
  // by current view mode and let fill convey the data signal.
  const pathOptions = isSelected
    ? { color: '#1d4ed8', weight: 3, fillColor: color, fillOpacity: 0.55 }
    : { color, weight: 1, fillColor: color, fillOpacity: 0.45 };

  const tooltipContent = viewMode === 'usage'
    ? `(${cell.x}, ${cell.y}) · ${cell.usage_count || 0} hits${cell.last_used ? ` · last ${new Date(cell.last_used).toLocaleString()}` : ''}`
    : `(${cell.x}, ${cell.y}) · ${(cell.cache_percentage * 100).toFixed(0)}% · ${fmtBytes(cell.size_bytes)} · ${cell.data_types.length}/${cell.data_types.length + cell.missing_data_types.length} types`;

  return (
    <Rectangle
      bounds={bounds}
      pathOptions={pathOptions}
      eventHandlers={{ click: (e) => { L.DomEvent.stopPropagation(e); onToggle(cell, e.originalEvent); } }}
    >
      <Tooltip sticky direction="top">{tooltipContent}</Tooltip>
    </Rectangle>
  );
};

const AdminCachePanel = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');
  // Map cellKey -> cell object. Storing the whole object (not just a flag)
  // lets us keep virtual cells (uncached, drawn from a rectangle) in the
  // selection — they aren't in `cells` from the overview, so a `cells.filter`
  // would drop them.
  const [selected, setSelected] = useState({});
  const [viewMode, setViewMode] = useState('coverage'); // 'coverage' | 'usage'
  const [rectangleMode, setRectangleMode] = useState(false);
  const fitNonceRef = useRef(0);
  const [fitNonce, setFitNonce] = useState(0);

  const fetchOverview = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/admin/cache/overview`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setOverview(data);
      // Re-fit on the first successful load and after refreshes that
      // change the cell envelope; cheap to bump every time.
      fitNonceRef.current += 1;
      setFitNonce(fitNonceRef.current);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  // Memoise so the [] fallback isn't a fresh array on every render —
  // otherwise the useMemos that depend on `cells` would re-run constantly.
  const cells = useMemo(() => overview?.cells || [], [overview]);
  const dataTypes = useMemo(() => overview?.data_types || [], [overview]);

  const selectedCells = useMemo(() => Object.values(selected), [selected]);

  // After every overview reload, replace any virtual selected cell with
  // its real counterpart if it now exists on disk. Without this the
  // selection panel keeps showing 0% / 0 bytes for cells that were just
  // fetched.
  useEffect(() => {
    if (cells.length === 0) return;
    const byKey = new Map();
    for (const c of cells) byKey.set(cellKey(c), c);
    setSelected((prev) => {
      let changed = false;
      const next = {};
      for (const [key, cell] of Object.entries(prev)) {
        const real = byKey.get(key);
        if (real && real !== cell) {
          next[key] = real;
          changed = true;
        } else {
          next[key] = cell;
        }
      }
      return changed ? next : prev;
    });
  }, [cells]);

  const maxUsage = useMemo(() => {
    let m = 0;
    for (const c of cells) if ((c.usage_count || 0) > m) m = c.usage_count;
    return m;
  }, [cells]);

  const toggleCell = (cell, event) => {
    setSelected((prev) => {
      const key = cellKey(cell);
      // Shift-click: additive only, never deselects — matches the typical
      // "lasso" behaviour admins reach for when bulk-cleaning a region.
      if (event && event.shiftKey) {
        return { ...prev, [key]: cell };
      }
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = cell;
      return next;
    });
  };

  const clearSelection = () => setSelected({});

  const selectAll = () => {
    const next = {};
    for (const c of cells) next[cellKey(c)] = c;
    setSelected(next);
  };

  const selectIncomplete = () => {
    const next = {};
    for (const c of cells) if (c.cache_percentage < 1) next[cellKey(c)] = c;
    setSelected(next);
  };

  // Translate a drawn lat/lon rectangle into the (x, y) cell grid using
  // the raster_size the backend reports. Existing cached cells are
  // pulled from `cells`; everything else becomes a virtual cell so the
  // user can still queue them for fetch.
  const handleRectangleSelect = ({ minLat, maxLat, minLon, maxLon }) => {
    const rasterSize = overview?.raster_size || 0.2;
    const minX = Math.floor(minLon / rasterSize);
    const maxX = Math.floor(maxLon / rasterSize);
    const minY = Math.floor(minLat / rasterSize);
    const maxY = Math.floor(maxLat / rasterSize);
    const byKey = new Map();
    for (const c of cells) byKey.set(cellKey(c), c);
    setSelected((prev) => {
      const next = { ...prev };
      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          const key = `${x}_${y}`;
          next[key] = byKey.get(key) || virtualCell(x, y, rasterSize, dataTypes);
        }
      }
      return next;
    });
  };

  const runFetch = async (typesOrNull) => {
    if (selectedCells.length === 0) return;
    if (!window.confirm(`Fetch missing data for ${selectedCells.length} cell(s)? This may take a while.`)) return;
    setBusy(true);
    setBusyMessage('Fetching missing data via Overpass…');
    try {
      const r = await fetch(`${API_URL}/admin/cache/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          cells: selectedCells.map((c) => [c.x, c.y]),
          data_types: typesOrNull,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = await r.json();
      console.log('Fetch result', result);
      await fetchOverview();
    } catch (e) {
      console.error(e);
      setError(`Fetch failed: ${e.message}`);
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  };

  const runCleanup = async () => {
    if (selectedCells.length === 0) return;
    const totalSize = selectedCells.reduce((a, c) => a + (c.size_bytes || 0), 0);
    if (!window.confirm(
      `Delete cache files for ${selectedCells.length} cell(s) (${fmtBytes(totalSize)})? This cannot be undone.`,
    )) return;
    setBusy(true);
    setBusyMessage('Deleting cache files…');
    try {
      const r = await fetch(`${API_URL}/admin/cache/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ cells: selectedCells.map((c) => [c.x, c.y]) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      clearSelection();
      await fetchOverview();
    } catch (e) {
      console.error(e);
      setError(`Cleanup failed: ${e.message}`);
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  };

  const runDownload = async () => {
    if (selectedCells.length === 0) return;
    setBusy(true);
    setBusyMessage('Packaging zip…');
    try {
      const r = await fetch(`${API_URL}/admin/cache/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ cells: selectedCells.map((c) => [c.x, c.y]) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // The server response is the binary zip — kick a download.
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedCells.length === 1
        ? `cache_cell_${selectedCells[0].x}_${selectedCells[0].y}.zip`
        : `cache_cells_${selectedCells.length}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError(`Download failed: ${e.message}`);
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  };

  // Aggregate stats for the toolbar header.
  const totalCells = cells.length;
  const fullCells = cells.filter((c) => c.cache_percentage >= 1).length;
  const totalSize = overview?.total_size_bytes || 0;
  const completeness = totalCells > 0
    ? cells.reduce((a, c) => a + c.cache_percentage, 0) / totalCells
    : 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 underline">dismiss</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center gap-2 text-sm">
          <HardDrive className="h-4 w-4 text-gray-500" />
          <span className="font-semibold">{fmtBytes(totalSize)}</span>
          <span className="text-gray-500">on disk</span>
        </div>
        <div className="text-sm text-gray-600">
          {totalCells} cell{totalCells === 1 ? '' : 's'} · {fullCells} fully cached · {(completeness * 100).toFixed(1)}% avg coverage
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-gray-200 bg-white p-0.5">
            <button
              onClick={() => setViewMode('coverage')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs ${
                viewMode === 'coverage' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MapIcon className="h-3.5 w-3.5" />
              Coverage
            </button>
            <button
              onClick={() => setViewMode('usage')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs ${
                viewMode === 'usage' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Usage
            </button>
          </div>
          <Button
            size="sm"
            variant={rectangleMode ? 'default' : 'outline'}
            onClick={() => setRectangleMode((v) => !v)}
            className={`rounded-xl ${rectangleMode ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}`}
            title="Draw a rectangle to select every cell inside (cached or not)"
          >
            <Square className="h-4 w-4 mr-1" />
            {rectangleMode ? 'Drawing… (click-drag)' : 'Draw rectangle'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={selectAll}
            disabled={cells.length === 0}
            className="rounded-xl"
          >
            Select all
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={selectIncomplete}
            disabled={cells.length === 0}
            className="rounded-xl"
          >
            Select incomplete
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchOverview}
            disabled={loading}
            className="rounded-xl"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <SelectionPanel
        selectedCells={selectedCells}
        allCells={cells}
        dataTypes={dataTypes}
        busy={busy}
        onFetch={runFetch}
        onCleanup={runCleanup}
        onDownload={runDownload}
        onClear={clearSelection}
      />

      {busy && busyMessage && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <RefreshCw className="h-4 w-4 animate-spin" />
          {busyMessage}
        </div>
      )}

      <div className="relative w-full h-[70vh] min-h-[420px] rounded-xl overflow-hidden border border-gray-200">
        <MapContainer
          center={[50, 10]}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitToCells cells={cells} nonce={fitNonce} />
          <RectangleSelector
            enabled={rectangleMode}
            onSelect={handleRectangleSelect}
            onDone={() => setRectangleMode(false)}
          />
          {cells.map((c) => {
            const key = cellKey(c);
            const isSelected = !!selected[key];
            const color = viewMode === 'usage'
              ? colorForUsage(c.usage_count || 0, maxUsage)
              : colorForPercentage(c.cache_percentage);
            return (
              <CellRectangle
                key={key}
                cell={c}
                isSelected={isSelected}
                color={color}
                onToggle={toggleCell}
                viewMode={viewMode}
              />
            );
          })}
          {/* Virtual cells (selected via rectangle, not yet on disk).
              Rendered with a dashed blue outline + transparent fill so
              they're obviously placeholders ready for a fetch. */}
          {selectedCells
            .filter((c) => c.virtual)
            .map((c) => {
              const [minLon, minLat, maxLon, maxLat] = c.bounds;
              return (
                <Rectangle
                  key={`virtual-${cellKey(c)}`}
                  bounds={[[minLat, minLon], [maxLat, maxLon]]}
                  pathOptions={{
                    color: '#1d4ed8',
                    weight: 2,
                    dashArray: '4 4',
                    fillColor: '#3b82f6',
                    fillOpacity: 0.18,
                  }}
                  eventHandlers={{
                    click: (e) => {
                      L.DomEvent.stopPropagation(e);
                      toggleCell(c, e.originalEvent);
                    },
                  }}
                >
                  <Tooltip sticky direction="top">
                    {`(${c.x}, ${c.y}) · uncached · click to deselect`}
                  </Tooltip>
                </Rectangle>
              );
            })}
        </MapContainer>

        <div className="pointer-events-none absolute bottom-3 left-3 rounded-xl bg-white/95 border border-gray-200 px-3 py-2 text-[11px] shadow-sm">
          {viewMode === 'coverage' ? (
            <>
              <div className="font-semibold mb-1">Cache coverage</div>
              <div className="flex items-center gap-1">
                <span style={{ background: 'rgb(255,0,0)' }} className="inline-block w-3 h-3 rounded-sm" /> 0%
                <span style={{ background: 'rgb(127,127,0)' }} className="inline-block w-3 h-3 rounded-sm ml-2" /> 50%
                <span style={{ background: 'rgb(0,255,0)' }} className="inline-block w-3 h-3 rounded-sm ml-2" /> 100%
              </div>
            </>
          ) : (
            <>
              <div className="font-semibold mb-1">Cell usage</div>
              <div className="flex items-center gap-1">
                <span style={{ background: '#9ca3af' }} className="inline-block w-3 h-3 rounded-sm" /> 0
                <span style={{ background: 'rgb(255,0,0)' }} className="inline-block w-3 h-3 rounded-sm ml-2" /> low
                <span style={{ background: 'rgb(0,255,0)' }} className="inline-block w-3 h-3 rounded-sm ml-2" /> max ({maxUsage})
              </div>
            </>
          )}
          <div className="mt-1 text-gray-600">
            Click cells to select · shift-click adds · Draw rectangle for uncached areas
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCachePanel;
